<template>
    <div class="flex flex-col items-center max-w-4xl mx-auto">
        <div v-if="!showInputs" class="w-full mt-8">
            <social-network-picker
                v-model="selectedIds"
                :options="pickerOptions"
            />

            <p v-if="error" class="text-theme-danger text-sm mt-4 text-center">
                {{ error }}
            </p>
        </div>

        <div v-else class="w-full mt-8">
            <button
                class="flex items-center gap-2 text-sm font-medium text-theme-muted hover:text-theme-base transition mb-6"
                @click="showInputs = false"
            >
                &larr; Cambiar selección
            </button>

            <VueDraggable
                v-model="selectedIds"
                tag="div"
                handle=".drag-handle"
                animation="200"
                class="space-y-4"
            >
                <div
                    v-for="network in selectedNetworks"
                    :key="network.key"
                    class="bg-theme-card border border-theme-border rounded-xl p-4"
                >
                    <label
                        class="text-sm font-medium text-theme-base flex items-center gap-3 mb-3"
                    >
                        <span
                            class="drag-handle cursor-grab select-none text-theme-muted"
                        >
                            ⠿
                        </span>
                        <img
                            v-if="network.icon"
                            :src="network.icon"
                            :alt="network.label"
                            class="w-5 h-5 object-contain"
                        />
                        {{ network.label }}
                    </label>
                    <whatsapp-number-input
                        v-if="network.key === 'whatsapp'"
                        v-model="formData.whatsapp"
                        :country.sync="whatsappCountry"
                        :pinned-country="defaultCountry"
                        :invalid="Boolean(errors.whatsapp)"
                        :placeholder="network.placeholder"
                    />
                    <div
                        v-else
                        class="flex items-center border border-theme-border rounded-lg overflow-hidden bg-theme-input"
                    >
                        <span
                            class="text-xs text-theme-muted bg-theme-hover px-3 py-2 border-r border-theme-border whitespace-nowrap select-none"
                        >
                            {{ network.baseUrl }}
                        </span>
                        <input
                            v-model="formData[network.key]"
                            type="text"
                            :placeholder="network.placeholder"
                            class="outline-none text-sm flex-1 px-3 py-2 bg-theme-input text-theme-base"
                        />
                    </div>
                    <p
                        v-if="errors[network.key]"
                        class="text-theme-danger text-xs mt-1"
                    >
                        {{ errors[network.key] }}
                    </p>
                </div>
            </VueDraggable>

            <p v-if="error" class="text-theme-danger text-sm mt-4">
                {{ error }}
            </p>
        </div>
    </div>
</template>

<script>
import onboardingCompletedGuard from "@/mixins/onboardingCompletedGuard";
import onboardingStepNavigation from "@/mixins/onboardingStepNavigation";
import VueDraggable from "vuedraggable";
import { mapActions, mapMutations } from "vuex";

import { SOCIAL_NETWORK_OPTIONS } from "@/common/fields/social-networks";
import {
    DEFAULT_WHATSAPP_COUNTRY,
    buildWhatsappNumber,
    isValidWhatsappLocalNumber,
    normalizeWhatsappCountryCode,
    splitWhatsappNumber
} from "@/common/lib/whatsapp";

import SocialNetworkPicker from "@/components/utils/SocialNetworkPicker.vue";
import WhatsappNumberInput from "@/components/utils/WhatsappNumberInput.vue";

const WHATSAPP_TOGGLE_OPTION = {
    key: "whatsapp",
    label: "WhatsApp",
    icon: "/img/networks-icons/whatsapp.png",
    placeholder: "3001234567"
};

const ALL_NETWORKS = [WHATSAPP_TOGGLE_OPTION, ...SOCIAL_NETWORK_OPTIONS];

export default {
    name: "OnboardingSocialNetworksPage",

    components: {
        SocialNetworkPicker,
        VueDraggable,
        WhatsappNumberInput
    },

    mixins: [onboardingCompletedGuard, onboardingStepNavigation],

    data() {
        return {
            stepKey: "social-networks",
            selectedIds: [],
            formData: {},
            whatsappCountry: DEFAULT_WHATSAPP_COUNTRY,
            defaultCountry: DEFAULT_WHATSAPP_COUNTRY,
            errors: {},
            error: null,
            isLoading: false,
            showInputs: false,
            pickerOptions: ALL_NETWORKS
        };
    },

    computed: {
        selectedNetworks() {
            return this.selectedIds
                .map((key) =>
                    ALL_NETWORKS.find((network) => network.key === key)
                )
                .filter(Boolean);
        },
        localStepperUi() {
            return {
                nextDisabled: false,
                loading: this.isLoading,
                nextLabel: "",
                nextVariant: "primary"
            };
        }
    },

    watch: {
        selectedIds() {
            this.syncSocialNetworksDraft();
            this.syncWhatsappDraft();
        },
        formData: {
            deep: true,
            handler() {
                this.syncSocialNetworksDraft();
                this.syncWhatsappDraft();
            }
        },
        whatsappCountry() {
            this.syncWhatsappDraft();
        }
    },

    async mounted() {
        await this.ensureOnboardingStatus();
        if (this.redirectIfOnboardingCompleted()) return;
        this.preloadFromSelections();
        // Si ya hay redes sociales guardadas, ir directo a editarlas en vez
        // de mostrar el picker de selección de nuevo (mismo criterio que el
        // editor: si ya hay algo, la acción es "editar", no "elegir").
        if (this.selectedIds.length) {
            this.showInputs = true;
        }
    },

    methods: {
        ...mapActions("onboarding", ["submitSocialNetworks"]),
        ...mapActions("auth", ["applyAllPermissions", "getProfile"]),
        ...mapMutations("onboarding", [
            "SET_DRAFT_SOCIAL_NETWORKS",
            "SET_DRAFT_WHATSAPP"
        ]),

        // Solo pasa el estado crudo (que redes eligio, que escribio en cada
        // una): la mutation decide si cada red va con URL de muestra o con
        // la URL completa.
        syncSocialNetworksDraft() {
            this.SET_DRAFT_SOCIAL_NETWORKS({
                selectedNetworks: this.selectedNetworks.filter(
                    (network) => network.key !== "whatsapp"
                ),
                formData: this.formData
            });
        },

        syncWhatsappDraft() {
            this.SET_DRAFT_WHATSAPP({
                selected: this.selectedIds.includes("whatsapp"),
                value: buildWhatsappNumber(
                    this.whatsappCountry,
                    (this.formData.whatsapp || "").trim()
                )
            });
        },

        preloadFromSelections() {
            const saved = this.selections;
            this.defaultCountry = normalizeWhatsappCountryCode(
                saved.defaultCountry
            );
            const { country, localNumber } = splitWhatsappNumber(
                saved.whatsappNumber,
                this.defaultCountry
            );
            this.whatsappCountry = country;
            if (localNumber) {
                this.$set(this.formData, "whatsapp", localNumber);
            }

            const savedNetworks = saved.socialNetworks || [];

            if (saved.whatsappNumber) {
                this.selectedIds.push("whatsapp");
            }

            for (const entry of savedNetworks) {
                const network = ALL_NETWORKS.find(
                    (n) => n.key === entry.network
                );
                if (!network || !entry.url) continue;
                const value =
                    network.baseUrl && entry.url.startsWith(network.baseUrl)
                        ? entry.url.slice(network.baseUrl.length)
                        : entry.url;
                this.$set(this.formData, entry.network, value);
                if (!this.selectedIds.includes(entry.network)) {
                    this.selectedIds.push(entry.network);
                }
            }
        },

        stepperNext() {
            if (this.readOnly) {
                this.goToNextReachable();
                return;
            }
            if (!this.showInputs && this.selectedIds.length) {
                this.showInputs = true;
                return;
            }
            this.continueStep();
        },

        validate() {
            this.errors = {};
            let valid = true;

            for (const network of this.selectedNetworks) {
                const val = (this.formData[network.key] || "").trim();
                if (!val) {
                    continue;
                }

                if (network.key === "whatsapp") {
                    if (
                        !isValidWhatsappLocalNumber(this.whatsappCountry, val)
                    ) {
                        this.errors[network.key] =
                            "Solo dígitos, sin el indicativo (mínimo 7)";
                        valid = false;
                    }
                } else if (!/^[^\s/]+/.test(val)) {
                    this.errors[network.key] = "Ingresa un usuario válido";
                    valid = false;
                }
            }
            return valid;
        },

        async continueStep() {
            if (this.isLoading) return;
            if (!this.validate()) return;

            const payload = {};
            const socialNetworks = [];

            for (const network of this.selectedNetworks) {
                const val = (this.formData[network.key] || "").trim();
                if (!val) continue;
                if (network.key === "whatsapp") {
                    payload.whatsappNumber = buildWhatsappNumber(
                        this.whatsappCountry,
                        val
                    );
                    payload.defaultCountry = this.whatsappCountry;
                } else {
                    socialNetworks.push({
                        network: network.key,
                        url: network.baseUrl + val
                    });
                }
            }
            payload.socialNetworks = socialNetworks;

            await this.saveSocialNetworks(payload);
        },

        async saveSocialNetworks(payload) {
            this.isLoading = true;
            this.error = null;
            try {
                const data = await this.submitSocialNetworks(payload);
                // El draft ya quedo con estos valores por los watchers de
                // selectedIds/formData — nada que confirmar aparte aca.
                await this.handleFlowResponse(data.onboardingFlow);
                this.isLoading = false;
            } catch (err) {
                this.isLoading = false;
                this.error =
                    err.message || "Error al guardar las redes sociales";
            }
        },

        async handleFlowResponse(flow) {
            if (!flow) return;
            if (flow.status === "completed") {
                try {
                    const profile = await this.getProfile();
                    if (profile.permissions) {
                        this.applyAllPermissions({
                            permissions: profile.permissions
                        });
                    }
                } catch {
                    // Permissions applied on next login
                }
                if (!flow.nextStep) {
                    this.$router.push(this.completionRoute);
                    return;
                }
                const route = this.routeForStep(flow.nextStep);
                if (route) {
                    this.$router.push(route);
                    return;
                }
                this.$router.push("/app");
                return;
            }
            const route = this.routeForStep(flow.step);
            if (route) {
                this.$router.push(route);
            }
        }
    }
};
</script>
